import { SIDEBAR_ITEMS } from "../assets/instateamMasterConfig";
import { currencyFormatter, getInitials, toAbsoluteUrl } from "../utils/adminHelpers";

export function LogoBlock({ subtitle, logo }) {
  return (
    <div className="logo-block">
      <img
        src={logo}
        alt="Insta Team Logo"
        className="logo-image"
      />

      
    </div>
  );
}

export function StatusBanner({ type, text, onClose }) {
  return (
    <div className={`status-banner ${type}`}>
      <span>{text}</span>
      {onClose ? (
        <button type="button" onClick={onClose}>
          <AppIcon name="close" />
        </button>
      ) : null}
    </div>
  );
}

export function Sidebar({ activeKey, items = SIDEBAR_ITEMS, onSelect, onLogout, notificationCounts = {} }) {
  return (
    <aside className="admin-sidebar">
      <div>
        <div className="admin-sidebar-top">
          <LogoBlock
    subtitle="HRMS & Management"
    logo="/masterlogo.png"
  />
        </div>

        <nav className="sidebar-nav">
          {items.map((item) => {
            const hasUnread = Boolean(item.notificationCategory && notificationCounts[`${item.notificationCategory}Unread`]);

            return (
              <button
                key={item.key}
                className={`sidebar-link ${activeKey === item.key ? "active" : ""}`}
                type="button"
                onClick={() => onSelect(item.key)}
              >
                <AppIcon name={item.icon} />
                <span>{item.label}</span>
                {hasUnread ? <i className="sidebar-unread-dot" aria-label="Unread updates" /> : null}
              </button>
            );
          })}
          <button className="sidebar-link" type="button" onClick={onLogout}>
            <AppIcon name="logout" />
            <span>Logout</span>
          </button>
        </nav>
      </div>
    </aside>
  );
}

export function Header({
  title,
  subtitle,
  admin,
  onOpenNotifications,
  onOpenProfile,
  onToggleSidebar,
  notificationCount = 0,
  showNotifications = true,
  searchText,
  onSearchChange,
  searchPlaceholder,
  searchDisabled,
}) {
  return (
    <header className="top-header">
      <div className="top-header-left">
        <button className="menu-toggle" type="button" onClick={onToggleSidebar} aria-label="Toggle sidebar">
          <AppIcon name="menu" />
        </button>

        <div className="top-search">
          <AppIcon name="search" />
          <input
            value={searchText}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            disabled={searchDisabled}
          />
        </div>
      </div>

      <div className="top-header-right">
        {showNotifications ? (
          <button className="icon-button" type="button" onClick={onOpenNotifications}>
            <AppIcon name="bell" />
            {notificationCount ? (
              <span className="notification-dot">{notificationCount}</span>
            ) : null}
          </button>
        ) : null}

        <button className="profile-chip" type="button" onClick={onOpenProfile}>
          <div className="avatar-circle small">{getInitials(admin?.fullName)}</div>
          <div className="profile-chip-copy">
            <strong>{admin?.fullName || "Admin User"}</strong>
            <span>{admin?.roleLabel || "Admin"}</span>
          </div>
          <AppIcon name="chevronDown" />
        </button>
      </div>

      <div className="page-copy">
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
    </header>
  );
}

export function MetricCard({ label, value, helper, icon, tone = "default" }) {
  return (
    <article className="metric-card">
      <div className={`metric-icon ${tone}`}>
        <AppIcon name={icon} />
      </div>
      <div className="metric-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <small>{helper}</small>
      </div>
    </article>
  );
}

export function LabeledInput({
  label,
  required = false,
  value,
  onChange,
  placeholder,
  icon,
  type = "text",
  trailingButton = null,
}) {
  return (
    <label className="form-field">
      <span>
        {label}
        {required ? <em>*</em> : null}
      </span>
      <div className="field-shell">
        {icon ? <AppIcon name={icon} /> : null}
        <input
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
        {trailingButton}
      </div>
    </label>
  );
}

export function SelectInput({
  label,
  required = false,
  value,
  onChange,
  options,
  placeholder,
}) {
  return (
    <label className="form-field">
      <span>
        {label}
        {required ? <em>*</em> : null}
      </span>
      <div className="field-shell select-shell">
        <select value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">{placeholder}</option>
          {options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
        <AppIcon name="chevronDown" />
      </div>
    </label>
  );
}

export function DateInput({ label, required = false, value, onChange }) {
  return (
    <label className="form-field">
      <span>
        {label}
        {required ? <em>*</em> : null}
      </span>
      <div className="field-shell">
        <input type="date" value={value} onChange={(event) => onChange(event.target.value)} />
        <AppIcon name="calendar" />
      </div>
    </label>
  );
}

export function PhoneInput({ value, onChange }) {
  return (
    <label className="form-field">
      <span>
        Phone Number <em>*</em>
      </span>
      <div className="phone-shell">
        <div className="country-chip">🇮🇳 +91</div>
        <input
          type="tel"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Enter phone number"
        />
      </div>
    </label>
  );
}

export function StaticInput({ label, required = false, value }) {
  return (
    <label className="form-field">
      <span>
        {label}
        {required ? <em>*</em> : null}
      </span>
      <div className="field-shell disabled-shell">
        <input value={value} disabled readOnly />
      </div>
    </label>
  );
}

export function ProfileUploadCard({ file, onSelect, existingFile = null }) {
  return (
    <div className="profile-upload-card">
      <label className="upload-picker-card">
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={(event) => onSelect(event.target.files?.[0] || null)}
          hidden
        />

        {existingFile?.url && !file ? (
          <img
            src={toAbsoluteUrl(existingFile.url)}
            alt="Employee profile"
            className="profile-upload-preview"
          />
        ) : (
          <div className="profile-upload-icon">
            <AppIcon name="upload" />
          </div>
        )}

        <strong>{file || existingFile?.url ? "Replace Photo" : "Upload Photo"}</strong>
        <span>{file ? file.name : existingFile?.originalName || "JPG, PNG up to 2MB"}</span>
      </label>

      {existingFile?.url ? (
        <a
          className="inline-file-link"
          href={toAbsoluteUrl(existingFile.url)}
          target="_blank"
          rel="noreferrer"
        >
          View current photo
        </a>
      ) : null}
    </div>
  );
}

export function DocumentUploadCard({ label, file, onSelect, existingFile = null }) {
  return (
    <div className="document-upload-card">
      <div className="document-upload-title">{label}</div>
      <p>JPG, PNG, PDF Max 2MB</p>

      {existingFile?.url ? (
        <a
          className="inline-file-link"
          href={toAbsoluteUrl(existingFile.url)}
          target="_blank"
          rel="noreferrer"
        >
          View current file
        </a>
      ) : (
        <span className="document-upload-placeholder">No file uploaded yet</span>
      )}

      <label className="document-upload-button">
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,application/pdf"
          onChange={(event) => onSelect(event.target.files?.[0] || null)}
          hidden
        />
        {file || existingFile?.url ? "Replace File" : "Upload"}
      </label>
      <small>{file ? file.name : existingFile?.originalName || ""}</small>
    </div>
  );
}

export function EmployeeDetailsModal({ employee, onClose }) {
  const documentEntries = Object.entries(employee.documents || {}).filter(
    ([, value]) => value?.url
  );

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="employee-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>Employee Overview</h3>
            <p>{employee.employeeId}</p>
          </div>
          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <div className="employee-modal-hero">
          {employee.profilePhoto?.url ? (
            <img
              src={toAbsoluteUrl(employee.profilePhoto.url)}
              alt={employee.fullName}
              className="modal-avatar"
            />
          ) : (
            <div className="avatar-circle modal-avatar">
              {getInitials(employee.fullName)}
            </div>
          )}
          <div>
            <h4>{employee.fullName}</h4>
            <p>
              {employee.designation} • {employee.department}
            </p>
            <span className={`status-badge ${employee.status}`}>{employee.status}</span>
          </div>
        </div>

        <div className="employee-modal-grid">
          <InfoBlock label="Username" value={employee.username} />
          <InfoBlock label="Email" value={employee.email} />
          <InfoBlock label="Phone" value={employee.phoneNumber} />
          <InfoBlock label="Date of Birth" value={employee.dateOfBirth} />
          <InfoBlock label="Gender" value={employee.gender} />
          <InfoBlock label="Marital Status" value={employee.maritalStatus || "—"} />
          <InfoBlock label="Date of Joining" value={employee.dateOfJoining} />
          <InfoBlock label="Employment Type" value={employee.employmentType} />
          <InfoBlock label="Reporting Manager" value={employee.reportingManager || "—"} />
          <InfoBlock label="Role" value={employee.role} />
          <InfoBlock
            label="Salary"
            value={currencyFormatter.format(Number(employee.basicSalary || 0))}
          />
          <InfoBlock label="Pay Frequency" value={employee.payFrequency} />
          <InfoBlock label="PAN Number" value={employee.panNumber || "—"} />
          <InfoBlock label="Bank Name" value={employee.bankName} />
          <InfoBlock label="Account Number" value={employee.accountNumber} />
          <InfoBlock label="IFSC Code" value={employee.ifscCode} />
          <InfoBlock
            label="Address"
            value={[
              employee.addressLine1,
              employee.addressLine2,
              employee.city,
              employee.state,
              employee.pinCode,
              employee.country,
            ]
              .filter(Boolean)
              .join(", ")}
          />
        </div>

        <div className="employee-documents">
          <h4>Documents</h4>
          {documentEntries.length ? (
            <div className="document-links">
              {documentEntries.map(([key, value]) => (
                <a
                  key={key}
                  className="document-link"
                  href={toAbsoluteUrl(value.url)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <AppIcon name="documents" />
                  <span>{value.originalName || key}</span>
                </a>
              ))}
            </div>
          ) : (
            <p className="documents-empty">No documents uploaded yet.</p>
          )}
        </div>
      </div>
    </div>
  );
}

export function InfoBlock({ label, value }) {
  return (
    <div className="info-block">
      <span>{label}</span>
      <strong>{value || "—"}</strong>
    </div>
  );
}

export function AppIcon({ name }) {
  const props = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    className: "app-icon",
  };

  switch (name) {
    case "dashboard":
      return (
        <svg {...props}>
          <path d="M4 13h6V4H4zM14 20h6v-9h-6zM14 10h6V4h-6zM4 20h6v-3H4z" />
        </svg>
      );
    case "employees":
    case "activeUsers":
    case "inactiveUsers":
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9.5" cy="7" r="3" />
          <path d="M19 8a3 3 0 0 1 0 6" />
          <path d="M16 21v-2a4 4 0 0 1 3-3.87" />
        </svg>
      );
    case "departments":
      return (
        <svg {...props}>
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case "designation":
      return (
        <svg {...props}>
          <path d="M4 21h16" />
          <path d="M7 21v-8l5-4 5 4v8" />
          <path d="M12 9V3" />
        </svg>
      );
    case "tasks":
      return (
        <svg {...props}>
          <rect x="4" y="4" width="16" height="16" rx="2" />
          <path d="m9 11 2 2 4-4" />
        </svg>
      );
    case "attendance":
    case "clock":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 3" />
        </svg>
      );
    case "leaves":
      return (
        <svg {...props}>
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M8 3v4M16 3v4M4 10h16" />
        </svg>
      );
    case "payroll":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M14.5 9.5c0-1.1-1-2-2.5-2s-2.5.9-2.5 2 1 2 2.5 2 2.5.9 2.5 2-1 2-2.5 2-2.5-.9-2.5-2" />
          <path d="M12 6v12" />
        </svg>
      );
    case "documents":
      return (
        <svg {...props}>
          <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
          <path d="M14 3v6h6" />
        </svg>
      );
    case "download":
      return (
        <svg {...props}>
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
      );
    case "refresh":
      return (
        <svg {...props}>
          <path d="M20 6v5h-5" />
          <path d="M4 18v-5h5" />
          <path d="M18 11a6 6 0 0 0-10-4.5L4 10" />
          <path d="M6 13a6 6 0 0 0 10 4.5L20 14" />
        </svg>
      );
    case "complaints":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v5" />
          <path d="M12 16h.01" />
        </svg>
      );
    case "chat":
      return (
        <svg {...props}>
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
        </svg>
      );
    case "announcements":
      return (
        <svg {...props}>
          <path d="M3 11v2a2 2 0 0 0 2 2h2l3 4V5L7 9H5a2 2 0 0 0-2 2Z" />
          <path d="M14 9a5 5 0 0 1 0 6" />
          <path d="M17 6a9 9 0 0 1 0 12" />
        </svg>
      );
    case "reports":
      return (
        <svg {...props}>
          <path d="M4 20V10" />
          <path d="M10 20V4" />
          <path d="M16 20v-7" />
          <path d="M22 20v-3" />
        </svg>
      );
    case "settings":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.7 1.7 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.82-.33 1.7 1.7 0 0 0-1 1.54V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1-1.54 1.7 1.7 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .33-1.82 1.7 1.7 0 0 0-1.54-1H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.54-1 1.7 1.7 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.82.33h.09A1.7 1.7 0 0 0 10 3.09V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.54 1.7 1.7 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.33 1.82v.09a1.7 1.7 0 0 0 1.54 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.54 1Z" />
        </svg>
      );
    case "logOut":
    case "logout":
      return (
        <svg {...props}>
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
          <path d="m10 17 5-5-5-5" />
          <path d="M15 12H3" />
        </svg>
      );
    case "support":
      return (
        <svg {...props}>
          <path d="M6 9a6 6 0 0 1 12 0v3a2 2 0 0 1-2 2h-1a2 2 0 0 0-2 2v1" />
          <path d="M6 14H5a2 2 0 0 1-2-2V9a9 9 0 0 1 18 0v3a2 2 0 0 1-2 2h-1" />
        </svg>
      );
    case "menu":
      return (
        <svg {...props}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case "search":
      return (
        <svg {...props}>
          <circle cx="11" cy="11" r="7" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      );
    case "bell":
      return (
        <svg {...props}>
          <path d="M15 17H5l1.4-1.4A2 2 0 0 0 7 14.17V11a5 5 0 1 1 10 0v3.17a2 2 0 0 0 .6 1.43L19 17h-4" />
          <path d="M10 20a2 2 0 0 0 4 0" />
        </svg>
      );
    case "mail":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m4 7 8 6 8-6" />
        </svg>
      );
    case "lock":
      return (
        <svg {...props}>
          <rect x="4" y="11" width="16" height="10" rx="2" />
          <path d="M8 11V8a4 4 0 1 1 8 0v3" />
        </svg>
      );
    case "eye":
      return (
        <svg {...props}>
          <path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12Z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "eyeOff":
      return (
        <svg {...props}>
          <path d="m3 3 18 18" />
          <path d="M10.58 10.58A2 2 0 0 0 13.42 13.42" />
          <path d="M9.88 5.09A10.94 10.94 0 0 1 12 5c6.5 0 10 7 10 7a16.45 16.45 0 0 1-4.31 5.16" />
          <path d="M6.61 6.61A16.2 16.2 0 0 0 2 12s3.5 7 10 7a10.94 10.94 0 0 0 5.39-1.39" />
        </svg>
      );
    case "calendar":
      return (
        <svg {...props}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      );
    case "upload":
      return (
        <svg {...props}>
          <path d="M12 16V5" />
          <path d="m7 10 5-5 5 5" />
          <path d="M20 16v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-2" />
        </svg>
      );
    case "plus":
      return (
        <svg {...props}>
          <path d="M12 5v14M5 12h14" />
        </svg>
      );
    case "check":
      return (
        <svg {...props}>
          <path d="m5 12 4 4L19 6" />
        </svg>
      );
    case "checkCheck":
      return (
        <svg {...props}>
          <path d="m3 12 4 4 3-3" />
          <path d="m10 12 4 4 7-8" />
        </svg>
      );
    case "code":
      return (
        <svg {...props}>
          <path d="m8 9-4 3 4 3" />
          <path d="m16 9 4 3-4 3" />
          <path d="m14 5-4 14" />
        </svg>
      );
    case "palette":
      return (
        <svg {...props}>
          <path d="M12 4a8 8 0 0 0 0 16h1a2 2 0 0 0 0-4h-1a2 2 0 0 1 0-4h2a6 6 0 0 0 0-12Z" />
          <circle cx="7.5" cy="11.5" r="1" />
          <circle cx="9.5" cy="8.5" r="1" />
          <circle cx="13.5" cy="8.5" r="1" />
        </svg>
      );
    case "filter":
      return (
        <svg {...props}>
          <path d="M4 5h16l-6 7v6l-4 2v-8z" />
        </svg>
      );
    case "export":
      return (
        <svg {...props}>
          <path d="M12 3v12" />
          <path d="m7 10 5 5 5-5" />
          <path d="M5 21h14" />
        </svg>
      );
    case "edit":
      return (
        <svg {...props}>
          <path d="M12 20h9" />
          <path d="m16.5 3.5 4 4L7 21H3v-4Z" />
        </svg>
      );
    case "reply":
      return (
        <svg {...props}>
          <path d="m9 17-5-5 5-5" />
          <path d="M4 12h9a6 6 0 0 1 6 6" />
        </svg>
      );
    case "forward":
      return (
        <svg {...props}>
          <path d="m15 17 5-5-5-5" />
          <path d="M20 12h-9a6 6 0 0 0-6 6" />
        </svg>
      );
    case "copy":
      return (
        <svg {...props}>
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <rect x="4" y="4" width="11" height="11" rx="2" />
        </svg>
      );
    case "trash":
      return (
        <svg {...props}>
          <path d="M3 6h18" />
          <path d="M8 6V4h8v2" />
          <path d="M19 6l-1 14H6L5 6" />
          <path d="M10 11v5M14 11v5" />
        </svg>
      );
    case "more":
      return (
        <svg {...props}>
          <circle cx="12" cy="5" r="1.5" fill="currentColor" />
          <circle cx="12" cy="12" r="1.5" fill="currentColor" />
          <circle cx="12" cy="19" r="1.5" fill="currentColor" />
        </svg>
      );
    case "chevronDown":
      return (
        <svg {...props}>
          <path d="m6 9 6 6 6-6" />
        </svg>
      );
    case "chevronLeft":
      return (
        <svg {...props}>
          <path d="m15 18-6-6 6-6" />
        </svg>
      );
    case "chevronRight":
      return (
        <svg {...props}>
          <path d="m9 18 6-6-6-6" />
        </svg>
      );
    case "arrowLeft":
      return (
        <svg {...props}>
          <path d="M19 12H5" />
          <path d="m12 19-7-7 7-7" />
        </svg>
      );
    case "close":
      return (
        <svg {...props}>
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      );
    case "shieldLock":
      return (
        <svg {...props}>
          <path d="M12 2 5 5v6c0 5 3.4 9.4 7 11 3.6-1.6 7-6 7-11V5z" />
          <rect x="9" y="11" width="6" height="5" rx="1" />
          <path d="M10 11V9a2 2 0 1 1 4 0v2" />
        </svg>
      );
    case "video":
      return (
        <svg {...props}>
          <rect x="3" y="6" width="13" height="12" rx="2" />
          <path d="m16 10 5-3v10l-5-3" />
        </svg>
      );
    case "infoCircle":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 10v5" />
          <path d="M12 7h.01" />
        </svg>
      );
    case "smile":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="M8 14s1.5 2 4 2 4-2 4-2" />
          <path d="M9 10h.01M15 10h.01" />
        </svg>
      );
    case "userPlus":
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9.5" cy="7" r="3" />
          <path d="M19 8v6M16 11h6" />
        </svg>
      );
    case "userMinus":
      return (
        <svg {...props}>
          <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
          <circle cx="9.5" cy="7" r="3" />
          <path d="M16 11h6" />
        </svg>
      );
    case "link":
      return (
        <svg {...props}>
          <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1" />
          <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 1 1-7-7l1-1" />
        </svg>
      );
    case "ban":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
          <path d="m6 6 12 12" />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
}
